import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { first } from 'rxjs';
import { ErrorModel } from '~api/models/error.model';
import { CredentialService } from '~api/services/credential.service';
import { Card } from '~app/provisioning/card/card';
import { Mfa } from '~app/provisioning/mfa/mfa';
import { Android } from '~app/provisioning/platforms/android/android';
import { Desktop } from '~app/provisioning/platforms/desktop/desktop';
import { Ios } from '~app/provisioning/platforms/ios/ios';
import { PlatformService } from '~app/services/platform.service';
import { CredentialInfo } from '~app/types/credential-info';
import { RedemptionInfo } from '~app/types/redemption-info';
import { CredentialRedemptionType, RedeemStatus } from '~generated-api/models';

@Component({
  selector: 'app-credential',
  imports: [
    TranslateModule,
    Card,
    Mfa,
    Android,
    Ios,
    Desktop
  ],
  templateUrl: './credential.html',
  styleUrl: './credential.css',
})
export class Credential {
  protected credentialInfo?: CredentialInfo;
  protected error?: ErrorModel;
  protected redemptionInfo?: RedemptionInfo;
  protected credentialKey?: string;
  protected readonly platform = inject(PlatformService);
  private readonly credential = inject(CredentialService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);

  constructor() {
    this.readRouteData();
  }

  protected onRedemptionInfoMfaResult(redemptionInfo: RedemptionInfo): void {
    this.redemptionInfo = redemptionInfo;
    this.refreshCredentialInfo();
  }

  protected onProviderError(errorMsg: string): void {
    Sentry.logger.error('onProviderError', { message: errorMsg });
    setTimeout(() => {
      this.error = new ErrorModel({ message: errorMsg, errorCode: 'provider' });
    }, 1);
  }

  protected onRetryAfterProviderError(): void {
    this.error = undefined;
  }

  private readRouteData(): void {
    this.route.params
      .pipe(first())
      .subscribe((params) => {
        this.credentialKey = params['credentialKey'];
        this.fetchInfo();
      });
  }

  private fetchInfo(): void {
    this.credentialInfo = undefined;
    this.redemptionInfo = undefined;
    this.credential.getCredentialInfo(this.credentialKey!).subscribe((credentialInfo) => {
      if (credentialInfo instanceof ErrorModel) {
        this.error = credentialInfo;
      } else {
        let card = credentialInfo.cardLogoAppleUrl;
        if (this.platform.isAndroid()) {
          card = credentialInfo.cardLogoGoogleUrl;
        }

        this.credentialInfo = {
          customerName: credentialInfo.customerName,
          card: card,
          provider: credentialInfo.provider,
        };

        this.redemptionInfo = {
          status: credentialInfo.status,
          types: credentialInfo.types,
        };

        this.refreshCredentialInfo();

        // If the credential is available we directly init the redemption
        if (credentialInfo.status === RedeemStatus.AVAILABLE) {
          this.initRedemption();
        }
      }
    });
  }

  private initRedemption(): void {
    this.redemptionInfo = undefined;
    this.error = undefined;
    this.credential.initiateCredentialRedemption(this.credentialKey!).subscribe((res) => {
      if (res instanceof ErrorModel) {
        this.error = res;
      } else {
        if ('id' in res) {
          this.redemptionInfo = {
            id: res.id,
            status: res.status,
            types: ('types' in res) ? res.types : undefined,
            result: res
          };
          this.refreshCredentialInfo();
        }
      }
    });
  }

  private refreshCredentialInfo() {
    let deployable = true;
    let subtitle = undefined;
    let errorSubtitle = undefined;
    if (this.platform.isAndroid()) {
      subtitle = this.translate.instant('credential.title_android', { name: this.credentialInfo?.customerName });
    } else if (this.platform.isIPhone()) {
      subtitle = this.translate.instant('credential.title_iphone', { name: this.credentialInfo?.customerName });
    } else if (this.platform.isIPad()) {
      subtitle = this.translate.instant('credential.title_ipad', { name: this.credentialInfo?.customerName });
    } else {
      subtitle = this.translate.instant('credential.title_other', { name: this.credentialInfo?.customerName });
    }

    if (this.platform.isAndroid() && !this.redemptionInfo?.types?.includes(CredentialRedemptionType.WALLET_ANDROID)) {
      deployable = false;
    } else if (this.platform.isIPhone() && !this.redemptionInfo?.types?.includes(CredentialRedemptionType.WALLET_IOS)) {
      deployable = false;
    } else if (this.redemptionInfo?.types?.length === 0 || !this.platform.isMobile()) {
      deployable = false;
    }

    if (this.redemptionInfo?.status === 'MFA_REQUIRED' && deployable) {
      subtitle = this.translate.instant('credential.title_mfa');
    } else if (this.redemptionInfo?.status === 'UNAVAILABLE') {
      subtitle = undefined;
      errorSubtitle = this.translate.instant('credential.title_unavailable');
    }

    this.credentialInfo = {
      ...this.credentialInfo,
      ...{
        title: this.credentialInfo?.customerName,
        subtitle,
        errorSubtitle,
        deployable,
      }
    };
  }
}
